/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import {
  Dialog as AriaDialog,
  Modal,
  ModalOverlay,
} from 'react-aria-components/Modal';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { PortalProvider } from '@/providers/portal';
import { DialogContext } from './context';
import styles from './styles.module.css';
import type { DialogProps } from './types';

/**
 * Dialog - A modal dialog component for important content and interactions
 *
 * Provides accessible modal functionality with focus management, backdrop handling,
 * and keyboard navigation. Supports multiple sizes and customizable dismissal behavior.
 * Perfect for confirmations, forms, or any content requiring user focus.
 *
 * @param props - The dialog props.
 * @param props.ref - Reference to the dialog element.
 * @param props.children - Dialog content (can be a render function with close callback).
 * @param props.classNames - Custom class names for sub-elements.
 * @param props.parentRef - Reference to parent element for portal rendering.
 * @param props.size - Size variant of the dialog.
 * @returns The dialog component.
 *
 * @example
 * ```tsx
 * // Basic dialog with trigger
 * <DialogTrigger>
 *   <Button>Open Dialog</Button>
 *   <Dialog>
 *     {({ close }) => (
 *       <>
 *         <DialogTitle>Confirm Action</DialogTitle>
 *         <p>Are you sure you want to continue?</p>
 *         <DialogFooter>
 *           <Button onPress={close}>Confirm</Button>
 *         </DialogFooter>
 *       </>
 *     )}
 *   </Dialog>
 * </DialogTrigger>
 * ```
 */
export function Dialog({ ref, ...props }: DialogProps) {
  [props, ref] = useContextProps(props, ref ?? null, DialogContext);

  const { children, classNames, parentRef, size = 'small', ...rest } = props;

  return (
    <DialogContext.Provider value={props}>
      <PortalProvider parentRef={parentRef}>
        <ModalOverlay
          {...rest}
          ref={ref}
          className={composeRenderProps(classNames?.overlay, (className) =>
            clsx('group/dialog', styles.overlay, className),
          )}
          data-size={size}
        >
          <Modal
            className={composeRenderProps(classNames?.modal, (className) =>
              clsx(styles.modal, className),
            )}
          >
            <AriaDialog className={clsx(styles.dialog, classNames?.dialog)}>
              {children}
            </AriaDialog>
          </Modal>
        </ModalOverlay>
      </PortalProvider>
    </DialogContext.Provider>
  );
}
